import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { 
  Target, Clock, TrendingUp, Sparkles, Lightbulb, 
  CheckCircle2, Zap, ArrowRight, ExternalLink, 
  Heart, Brain, Activity, CheckCircle
} from 'lucide-react';
import { HealthAction, ForYouItem } from 'types';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { persistence } from 'services/persistence';

interface ShareableHealthActionPageProps {
  actionId: string;
}

const FOR_YOU_ITEMS_STORAGE_KEY = 'eir-for-you-items';

const addToForYouPage = (item: ForYouItem): boolean => {
  const existingItems = persistence.getJSON<ForYouItem[]>(FOR_YOU_ITEMS_STORAGE_KEY, []);

  const alreadyExists = existingItems.some(
    existing => existing.id === item.id || 
    (existing.type === item.type && existing.title === item.title)
  );

  if (!alreadyExists) {
    existingItems.unshift(item);
    persistence.setJSON(FOR_YOU_ITEMS_STORAGE_KEY, existingItems);
    return true;
  }
  return false;
};

export const ShareableHealthActionPage: React.FC<ShareableHealthActionPageProps> = ({ actionId }) => {
  const [action, setAction] = useState<HealthAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedToForYou, setSavedToForYou] = useState(false);

  useEffect(() => {
    const loadAction = async () => {
      try {
        let decodedAction: HealthAction | null = null;
        
        // First, try to decode as base64-encoded JSON (preferred method for sharing)
        // This allows the action to be shared with anyone via URL
        try {
          const base64Decoded = atob(actionId);
          // Decode from UTF-8 (reverse of encodeURIComponent)
          const decoded = decodeURIComponent(base64Decoded);
          const parsed = JSON.parse(decoded);
          // Validate it's a HealthAction
          if (parsed && typeof parsed === 'object' && parsed.title && parsed.instructions) {
            decodedAction = parsed as HealthAction;
          }
        } catch (base64Error) {
          // Not base64 encoded, might be a direct ID - try localStorage
          // This is for backward compatibility or direct links
          const actions = persistence.getJSON<HealthAction[]>(STORAGE_KEYS.HEALTH_ACTIONS, []);
          decodedAction = actions.find(a => a.id === actionId) || null;
        }
        
        if (decodedAction) {
          // Generate a new unique ID for this action to avoid conflicts
          const newActionId = `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const actionWithNewId: HealthAction = {
            ...decodedAction,
            id: newActionId,
            createdAt: new Date().toISOString(),
            completed: false,
          };
          
          // Save action to localStorage for the person viewing the shared link
          // This works for anyone who clicks the link, not just the original creator
          const existingActions = persistence.getJSON<HealthAction[]>(STORAGE_KEYS.HEALTH_ACTIONS, []);

          // Check if a similar action already exists (by title and description)
          // This prevents duplicates if someone clicks the same shared link multiple times
          const isDuplicate = existingActions.some(
            a => a.title === actionWithNewId.title && 
                 a.description === actionWithNewId.description
          );

          if (!isDuplicate) {
            existingActions.unshift(actionWithNewId);
            persistence.setJSON(STORAGE_KEYS.HEALTH_ACTIONS, existingActions);
          } else {
            const existing = existingActions.find(
              a => a.title === actionWithNewId.title && 
                   a.description === actionWithNewId.description
            );
            if (existing) {
              actionWithNewId.id = existing.id;
            }
          }

          const forYouItem: ForYouItem = {
            id: `health-action:${actionWithNewId.id}`,
            title: actionWithNewId.title,
            description: actionWithNewId.description,
            type: 'health-action',
            sourcePage: 'health-actions',
            tags: [actionWithNewId.category, actionWithNewId.difficulty].filter(Boolean),
            addedAt: new Date().toISOString(),
          };

          addToForYouPage(forYouItem);
          setSavedToForYou(true);

          // Trigger a storage event so other tabs can pick up the change
          window.dispatchEvent(new StorageEvent('storage', {
            key: FOR_YOU_ITEMS_STORAGE_KEY,
            newValue: persistence.getItem(FOR_YOU_ITEMS_STORAGE_KEY)
          }));
          
          setAction(actionWithNewId);
        } else {
          setError('Health action not found. The link may be invalid or expired.');
        }
      } catch (err) {
        console.error('Failed to load health action:', err);
        setError('Failed to load health action. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadAction();
  }, [actionId]);

  const handleGetStarted = () => {
    // If they're already on eir.space, navigate to the app
    if (window.location.hostname.includes('eir.space') || window.location.hostname === 'localhost') {
      window.location.href = '/';
    } else {
      window.open('https://chat.eir.space', '_blank', 'noopener,noreferrer');
    }
  };

  const handleGoToForYou = () => {
    // Navigate to For You page if on same domain
    if (window.location.hostname.includes('eir.space') || window.location.hostname === 'localhost') {
      window.location.href = '/';
    } else {
      window.open('https://chat.eir.space/', '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex flex-col bg-muted">
        <div className="flex-1 flex flex-col items-center justify-center p-6 w-full max-w-[1200px] mx-auto">
          <div className="flex flex-col items-center justify-center gap-4 text-primary py-16 px-8">
            <p className="text-lg font-medium">Loading health action...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error || !action) {
    return (
      <main className="min-h-screen flex flex-col bg-muted">
        <div className="flex-1 flex flex-col items-center justify-center p-6 w-full max-w-[1200px] mx-auto">
          <Card className="text-center py-12 px-8 bg-card/95 rounded-xl border max-w-[600px] w-full">
            <CardContent className="pt-6">
              <h2 className="text-xl font-bold text-foreground-strong mb-2">Action Not Found</h2>
              <p className="text-foreground-muted mb-6">{error || 'The health action you\'re looking for doesn\'t exist.'}</p>
              <Button className="mt-6 py-6 px-10 w-full text-lg font-bold shadow-lg" onClick={handleGetStarted}>
                <ExternalLink size={20} />
                Visit Eir.space
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex flex-col bg-muted">
      <div className="flex-1 flex flex-col items-center justify-center p-6 w-full max-w-[1200px] mx-auto">
        <div className="text-center mb-12">
          <Badge variant="outline" className="inline-flex items-center gap-2 py-2 px-5 rounded-full border-primary/30 text-primary text-sm font-semibold mb-6">
            <Zap size={18} />
            Powered by Eir.space
          </Badge>
          <h1 className="text-2xl font-bold tracking-tight leading-tight m-0 mb-4 text-primary">Shared Health Action</h1>
          <p className="text-lg text-foreground-muted leading-relaxed m-0 max-w-[700px] mx-auto">
            Someone shared this personalized health action with you. It's been automatically saved to your Eir.space account and added to your "For You" page.
          </p>
          {savedToForYou && (
            <div className="mt-4 py-4 px-6 bg-green-50 border-green-500/30 rounded-xl flex flex-col items-start gap-3 text-green-800 text-sm font-medium">
              <div className="flex items-center gap-2 w-full">
                <CheckCircle size={20} />
                <strong>Saved to your Eir.space account!</strong>
              </div>
              <p>
                This health action has been added to your "For You" page. You can access it anytime by visiting Eir.space.
              </p>
            </div>
          )}
        </div>

        <Card className="bg-card/95 backdrop-blur-sm rounded-xl p-8 border shadow-lg w-full max-w-[800px]">
          <CardContent className="p-0">
          <div className="mb-8">
            <Badge className="inline-flex items-center gap-2 py-2 px-4 rounded-xl bg-primary text-white text-sm font-semibold uppercase tracking-[0.5px] mb-4">
              <Target size={14} />
              {action.category}
            </Badge>
            <h2 className="text-xl font-bold text-foreground-strong m-0 mb-4 leading-[1.3]">{action.title}</h2>
            <p className="text-base text-foreground-muted leading-loose m-0 mb-8">{action.description}</p>
          </div>

          <div className="flex gap-6 flex-wrap mb-8 p-6 bg-background-soft/50 rounded-xl border-primary/15">
            <div className="flex items-center gap-2 text-foreground-muted text-sm font-medium">
              <Clock size={20} />
              <strong>Time:</strong> {action.estimatedTime}
            </div>
            <div className="flex items-center gap-2 text-foreground-muted text-sm font-medium">
              <Target size={20} />
              <strong>Difficulty:</strong> {action.difficulty === 'easy' ? 'Easy' : action.difficulty === 'medium' ? 'Medium' : 'Advanced'}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-foreground-strong m-0 mb-4 flex items-center gap-3">
              <TrendingUp size={24} />
              Benefits
            </h3>
            <ul className="list-none p-0 m-0 grid gap-3">
              {action.benefits.map((benefit: string, index: number) => (
                <li className="flex items-start gap-3 py-4 px-5 bg-green-50 rounded-xl border-l-4 border-green-500 text-green-800 text-base leading-relaxed font-medium" key={index}>
                  <CheckCircle2 size={20} className="shrink-0 mt-0.5" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-foreground-strong m-0 mb-4 flex items-center gap-3">
              <Sparkles size={24} />
              Step-by-Step Instructions
            </h3>
            <ol className="list-none p-0 m-0 grid gap-4">
              {action.instructions.map((instruction: string, index: number) => (
                <li className="flex items-start gap-4 py-5 px-6 bg-background-soft/50 rounded-xl border-l-4 border-primary relative" key={index}>
                  <span>{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          {action.tips && action.tips.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-foreground-strong m-0 mb-4 flex items-center gap-3">
                <Lightbulb size={24} />
                Pro Tips
              </h3>
              <ul className="list-none p-0 m-0 grid gap-3">
                {action.tips.map((tip: string, index: number) => (
                  <li className="flex items-start gap-3 py-4 px-5 bg-amber-50 rounded-xl border-l-4 border-amber-500 text-amber-900 text-sm leading-relaxed font-medium" key={index}>
                    <Lightbulb size={18} className="shrink-0 mt-0.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {savedToForYou ? (
              <Button className="mt-8 py-6 px-10 w-full text-lg font-bold shadow-lg bg-green-600 hover:bg-green-700 text-white" onClick={handleGoToForYou}>
                <Target size={20} />
                View in "For You" Page
                <ArrowRight size={20} />
              </Button>
            ) : (
              <Button className="mt-8 py-6 px-10 w-full text-lg font-bold shadow-lg" onClick={handleGetStarted}>
                Get Started with Eir.space
                <ArrowRight size={20} />
              </Button>
            )}
          </div>
          </CardContent>
        </Card>

        <Card className="mt-16 py-12 px-8 bg-white/70 backdrop-blur-sm rounded-xl border text-center">
          <h3 className="text-xl font-bold text-foreground-strong m-0 mb-4 flex items-center justify-center gap-3">
            <Heart size={28} color="#ef4444" />
            Transform Your Health Journey
          </h3>
          <p className="text-lg text-foreground-muted leading-loose m-0 mb-6">
            Eir.space is your personal AI health assistant that helps you understand, track, and improve your health through personalized insights and actionable steps.
          </p>
          
          <div className="grid gap-6 my-8 mx-0">
            <div className="p-6 bg-background-soft/50 rounded-xl border-primary/15 text-center">
              <Brain size={32} />
              <h4 className="text-base font-semibold text-foreground-strong">AI-Powered Insights</h4>
              <p>Get personalized health recommendations based on your unique data</p>
            </div>
            <div className="p-6 bg-background-soft/50 rounded-xl border-primary/15 text-center">
              <Activity size={32} />
              <h4 className="text-base font-semibold text-foreground-strong">Track Progress</h4>
              <p>Monitor your health journey with comprehensive tracking tools</p>
            </div>
            <div className="p-6 bg-background-soft/50 rounded-xl border-primary/15 text-center">
              <Target size={32} />
              <h4 className="text-base font-semibold text-foreground-strong">Actionable Steps</h4>
              <p>Receive clear, actionable health actions tailored to your needs</p>
            </div>
            <div className="p-6 bg-background-soft/50 rounded-xl border-primary/15 text-center">
              <Heart size={32} />
              <h4 className="text-base font-semibold text-foreground-strong">Your Health, Your Way</h4>
              <p>Take control of your health with tools designed for you</p>
            </div>
          </div>

          <Button className="mt-8 py-6 px-10 w-full text-lg font-bold shadow-lg" onClick={handleGetStarted}>
            Start Your Health Journey
            <ArrowRight size={20} />
          </Button>
        </Card>
      </div>
    </main>
  );
};

